import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from '@thoughtspot/i18n';
import { EMAIL_CUSTOMIZATION_CONSTANTS } from './constants';
import { DEFAULT_SETTINGS, trackEmailCustomisationCancelled, trackEmailCustomisationEditSaved, trackEmailCustomisationEditTriggered, trackEmailCustomisationSaveFailed, } from './email-customization-analytics';
import { EmailCustomizationWizard } from './email-customization-wizard';
import { validateLogoUrl, validatePhoneNumber, validateUrl } from './email-customization-utils';
export const EmailCustomizationWizardContainer = ({ isOpen, onClose, onSave, initialSettings, orgId, allOrgsSettings, }) => {
    const { t } = useTranslation();
    const [settings, setSettings] = useState(initialSettings);
    const [isSaving, setIsSaving] = useState(false);
    const [isResetToDefault, setIsResetToDefault] = useState(false);
    const [logoUrlError, setLogoUrlError] = useState('');
    const [phoneNumberError, setPhoneNumberError] = useState('');
    const [companyWebsiteUrlError, setCompanyWebsiteUrlError] = useState('');
    const [contactSupportUrlError, setContactSupportUrlError] = useState('');
    const [privacyPolicyUrlError, setPrivacyPolicyUrlError] = useState('');
    const [editOpenTime, setEditOpenTime] = useState(0);
    const [trackedInitialSettings, setTrackedInitialSettings] = useState(initialSettings);
    useEffect(() => {
        setSettings(initialSettings);
        setLogoUrlError('');
        setPhoneNumberError('');
        setCompanyWebsiteUrlError('');
        setContactSupportUrlError('');
        setPrivacyPolicyUrlError('');
        setIsResetToDefault(false);
        setTrackedInitialSettings(initialSettings);
    }, [initialSettings]);
    useEffect(() => {
        if (isOpen) {
            const hasCustomisation = orgId !== undefined && orgId !== '-1';
            const customisationID = hasCustomisation ? orgId : 0;
            trackEmailCustomisationEditTriggered(hasCustomisation, customisationID);
            setEditOpenTime(Date.now());
        }
    }, [isOpen, orgId]);
    const updateSettings = useCallback((newSettings) => {
        setSettings(prev => ({ ...prev, ...newSettings }));
        if (isResetToDefault) {
            setIsResetToDefault(false);
        }
    }, [isResetToDefault]);
    const handleLogoUrlChange = useCallback((e) => {
        const logoUrl = e.target.value;
        const error = validateLogoUrl(logoUrl, t);
        setLogoUrlError(error);
        if (!error) {
            const shouldUseDefault = !logoUrl ||
                logoUrl === EMAIL_CUSTOMIZATION_CONSTANTS.DEFAULT_LOGO_URL;
            updateSettings({
                logo: shouldUseDefault
                    ? EMAIL_CUSTOMIZATION_CONSTANTS.SAME_AS_STYLING
                    : logoUrl
            });
        }
    }, [updateSettings, t]);
    const handlePhoneNumberChange = useCallback((e) => {
        const phoneNumber = e.target.value;
        const error = validatePhoneNumber(phoneNumber, t);
        setPhoneNumberError(error);
        if (!error) {
            updateSettings({ mobileNumber: phoneNumber });
        }
    }, [updateSettings, t]);
    const handleCompanyWebsiteUrlChange = useCallback((e) => {
        const url = e.target.value;
        const error = validateUrl(url, t);
        setCompanyWebsiteUrlError(error);
        if (!error) {
            updateSettings({ companyWebsiteUrl: url });
        }
    }, [updateSettings, t]);
    const handleContactSupportUrlChange = useCallback((e) => {
        const url = e.target.value;
        const error = validateUrl(url, t);
        setContactSupportUrlError(error);
        if (!error) {
            updateSettings({ contactSupportUrl: url });
        }
    }, [updateSettings, t]);
    const handlePrivacyPolicyUrlChange = useCallback((e) => {
        const url = e.target.value;
        const error = validateUrl(url, t);
        setPrivacyPolicyUrlError(error);
        if (!error) {
            updateSettings({ webLinkAddress1: url });
        }
    }, [updateSettings, t]);
    const handleSave = useCallback(async () => {
        try {
            setIsSaving(true);
            const duration = Date.now() - editOpenTime;
            const oldCustomisationID = orgId || 0;
            const newCustomisationID = orgId || 0;
            if (isResetToDefault) {
                await onSave({ ...settings, isResetToDefault: true });
                trackEmailCustomisationEditSaved(trackedInitialSettings, DEFAULT_SETTINGS, oldCustomisationID, 0, duration, true);
            }
            else {
                await onSave(settings);
                trackEmailCustomisationEditSaved(trackedInitialSettings, settings, oldCustomisationID, newCustomisationID, duration, false);
            }
        }
        catch (error) {
            const duration = Date.now() - editOpenTime;
            const oldCustomisationID = orgId || 0;
            const newCustomisationID = orgId || 0;
            const errorCode = error?.message || 'UNKNOWN_ERROR';
            trackEmailCustomisationSaveFailed(trackedInitialSettings, settings, oldCustomisationID, newCustomisationID, duration, isResetToDefault, errorCode);
        }
        finally {
            setIsSaving(false);
        }
    }, [isResetToDefault, settings, onSave, editOpenTime, trackedInitialSettings, orgId]);
    const handleReset = useCallback(() => {
        const resetToSettings = (orgId && orgId !== '-1' && allOrgsSettings)
            ? allOrgsSettings
            : DEFAULT_SETTINGS;
        setSettings(resetToSettings);
        setLogoUrlError('');
        setPhoneNumberError('');
        setCompanyWebsiteUrlError('');
        setContactSupportUrlError('');
        setPrivacyPolicyUrlError('');
        setIsResetToDefault(true);
    }, [orgId, allOrgsSettings]);
    const handleCancel = useCallback(() => {
        const duration = Date.now() - editOpenTime;
        const oldCustomisationID = orgId || 0;
        const newCustomisationID = orgId || 0;
        trackEmailCustomisationCancelled(trackedInitialSettings, settings, oldCustomisationID, newCustomisationID, duration, isResetToDefault);
        setSettings(initialSettings);
        setLogoUrlError('');
        setPhoneNumberError('');
        setCompanyWebsiteUrlError('');
        setContactSupportUrlError('');
        setPrivacyPolicyUrlError('');
        setIsResetToDefault(false);
        onClose();
    }, [initialSettings, onClose, editOpenTime, trackedInitialSettings, settings, orgId, isResetToDefault]);
    return (React.createElement(EmailCustomizationWizard, { isOpen: isOpen, onClose: handleCancel, onSave: handleSave, onReset: handleReset, settings: settings, updateSettings: updateSettings, isSaving: isSaving, isResetToDefault: isResetToDefault, logoUrlError: logoUrlError, onLogoUrlChange: handleLogoUrlChange, phoneNumberError: phoneNumberError, onPhoneNumberChange: handlePhoneNumberChange, companyWebsiteUrlError: companyWebsiteUrlError, onCompanyWebsiteUrlChange: handleCompanyWebsiteUrlChange, contactSupportUrlError: contactSupportUrlError, onContactSupportUrlChange: handleContactSupportUrlChange, privacyPolicyUrlError: privacyPolicyUrlError, onPrivacyPolicyUrlChange: handlePrivacyPolicyUrlChange }));
};
export default EmailCustomizationWizardContainer;
//# sourceMappingURL=email-customization-wizard.container.js.map